/** Strażnicy tras: sesja, wymuszona zmiana hasła, wymagana rola. */

import type { ReactNode } from 'react'
import { Navigate, Outlet, useLocation } from 'react-router-dom'

import type { ModuleSlug, UserRole } from '@/api/types'
import { useAuth } from '@/auth/AuthContext'
import { ModuleDisabledNotice } from '@/components/layout/ModuleDisabledNotice'
import { FullScreenLoader, Spinner } from '@/components/ui/Spinner'
import { useModules } from '@/hooks/useModules'

/** Wymaga zalogowania. Zapamiętuje docelowy adres, by wrócić po zalogowaniu. */
export function RequireAuth({ children }: { children?: ReactNode }) {
  const { isAuthenticated, isLoading, mustChangePassword } = useAuth()
  const location = useLocation()

  if (isLoading) return <FullScreenLoader label="Wczytywanie sesji…" />

  if (!isAuthenticated) {
    return <Navigate to="/logowanie" state={{ from: location.pathname }} replace />
  }

  // Konto po resecie hasła nie może korzystać z systemu przed jego zmianą.
  if (mustChangePassword && location.pathname !== '/zmiana-hasla') {
    return <Navigate to="/zmiana-hasla" replace />
  }

  return <>{children ?? <Outlet />}</>
}

/** Wymaga jednej ze wskazanych ról. Backend i tak weryfikuje to niezależnie. */
export function RequireRole({ roles, children }: { roles: UserRole[]; children?: ReactNode }) {
  const { user } = useAuth()

  if (!user) return <Navigate to="/logowanie" replace />

  if (!roles.includes(user.role)) {
    return <Navigate to="/brak-dostepu" replace />
  }

  return <>{children ?? <Outlet />}</>
}

/**
 * Wymaga konta serwisowego wykonawcy.
 *
 * Osobno od `RequireRole`, bo to nie jest rola: konto serwisowe ma rolę
 * właścicielki, więc warunek po roli wpuściłby całą administrację.
 */
export function RequireService({ children }: { children?: ReactNode }) {
  const { user } = useAuth()

  if (!user) return <Navigate to="/logowanie" replace />
  if (!user.is_service) return <Navigate to="/brak-dostepu" replace />

  return <>{children ?? <Outlet />}</>
}

/**
 * Wymaga włączonego modułu.
 *
 * Nie przekierowuje na „brak dostępu” — to nie kwestia uprawnień, tylko
 * zakresu, z jakiego szkoła korzysta. Zamiast tego pokazuje wyjaśnienie
 * w miejscu, w którym użytkownik się znalazł.
 */
export function RequireModule({ slug, children }: { slug: ModuleSlug; children?: ReactNode }) {
  const { data, isPending } = useModules()

  // Czekamy na odpowiedź, zamiast wpuszczać sekcję „na próbę”. Wpuszczona
  // zdążyłaby wysłać zapytania, które backend i tak odrzuca — użytkownik
  // zobaczyłby błędy, zanim pojawi się wyjaśnienie.
  if (isPending) {
    return (
      <div className="flex justify-center py-24">
        <Spinner className="h-8 w-8 text-primary" label="Sprawdzanie modułów…" />
      </div>
    )
  }

  // Nieudany odczyt listy nie może zablokować pracy — wtedy decyduje backend.
  if (!data) return <>{children ?? <Outlet />}</>

  const modul = data.find((item) => item.slug === slug)
  if (modul && !modul.is_enabled) return <ModuleDisabledNotice name={modul.name} />

  return <>{children ?? <Outlet />}</>
}

/** Zalogowany użytkownik nie powinien oglądać ekranu logowania. */
export function RedirectIfAuthenticated({ children }: { children: ReactNode }) {
  const { isAuthenticated, isLoading } = useAuth()

  if (isLoading) return <FullScreenLoader label="Wczytywanie…" />
  if (isAuthenticated) return <Navigate to="/" replace />

  return <>{children}</>
}
